import type { TrackPoint } from "../../../../lib/offline-draft";
import type { GeoArResult } from "../lab-v14/types/GeoArResult";
import type { GeoArFieldOrientation } from "./useGeoArFieldSensors";
import { exportTextFile, type TextExportFile } from "./nativeTextExport";

const STORAGE_KEY = "thera.geoar.field-session.v1";
const SCHEMA_VERSION = "1.0.0" as const;
const ROUTE_SAMPLE_INTERVAL_MS = 10_000;
const MAX_SESSIONS = 8;
const MAX_ROUTE_SAMPLES = 4_000;
const MAX_AR_SAMPLES = 5_000;
const MAX_EVIDENCE = 500;
const lastRouteSampleAtBySession = new Map<string, number>();

export type FieldSessionState = "recording" | "paused" | "finished" | "discarded" | "standalone";
export type FieldEvidenceKind = "VISIBLE" | "NO_VISIBLE" | "MARK_POINT" | "POINT_IDENTIFY" | "OBSERVATION";

export type FieldRouteContext = {
  activityId: string | null;
  routeId: string | null;
  routeName: string;
  recordingMode: "selected-route" | "free" | null;
  startedAt: number | null;
  state: "recording" | "paused" | "idle";
  distanceMeters: number;
  track: TrackPoint[];
};

export type FieldSessionLink = {
  sessionKey: string;
  activityId: string | null;
  routeId: string | null;
  routeName: string;
  recordingMode: "selected-route" | "free" | null;
  startedAt: number;
  state: FieldSessionState;
};

export type FieldRouteSample = {
  timestamp: number;
  latitude: number;
  longitude: number;
  altitude: number | null;
  accuracy: number;
  activeSeconds: number | null;
  distanceMeters: number;
};

export type FieldArSample = {
  timestamp: number;
  online: boolean;
  packId: string;
  position: {
    latitude: number;
    longitude: number;
    altitude: number | null;
    accuracy: number;
    sourceDevice: string;
  };
  orientation: GeoArFieldOrientation;
  calibration: {
    horizontalFov: number;
    verticalFov: number;
    headingOffset: number;
    pitchOffset: number;
  };
  selectedFeatureUuid: string | null;
  selectedResult: GeoArResult | null;
};

export type FieldEvidenceSnapshot = {
  timestamp: number;
  online: boolean;
  packId: string;
  position: FieldArSample["position"] | null;
  orientation: GeoArFieldOrientation | null;
  calibration: FieldArSample["calibration"];
  selectedFeatureUuid: string | null;
  selectedResult: GeoArResult | null;
  completeness: "complete" | "partial";
  missingFields: string[];
  positionSource: "geo-ar-gps" | "route-track" | "missing";
};

export type FieldEvidence = FieldEvidenceSnapshot & {
  evidenceId: string;
  kind: FieldEvidenceKind;
  notes: string;
  identifiedResult: GeoArResult | null;
  metadata: Record<string, string | number | boolean | null>;
};

export type FieldSessionRecord = {
  schemaVersion: typeof SCHEMA_VERSION;
  fieldSessionId: string;
  sessionKey: string;
  activityId: string | null;
  routeId: string | null;
  routeName: string;
  recordingMode: "selected-route" | "free" | null;
  state: FieldSessionState;
  startedAt: number;
  finishedAt: number | null;
  createdAt: number;
  updatedAt: number;
  routeSamples: FieldRouteSample[];
  arSamples: FieldArSample[];
  evidence: FieldEvidence[];
};

type FieldSessionStore = {
  schemaVersion: typeof SCHEMA_VERSION;
  sessions: FieldSessionRecord[];
};

function safeParseStore(raw: string | null): FieldSessionStore {
  if (!raw) return { schemaVersion: SCHEMA_VERSION, sessions: [] };
  try {
    const value = JSON.parse(raw) as Partial<FieldSessionStore>;
    if (!Array.isArray(value.sessions)) return { schemaVersion: SCHEMA_VERSION, sessions: [] };
    return {
      schemaVersion: SCHEMA_VERSION,
      sessions: value.sessions.filter((session): session is FieldSessionRecord => Boolean(session && typeof session.sessionKey === "string")),
    };
  } catch {
    return { schemaVersion: SCHEMA_VERSION, sessions: [] };
  }
}

function loadStore(): FieldSessionStore {
  if (typeof window === "undefined") return { schemaVersion: SCHEMA_VERSION, sessions: [] };
  return safeParseStore(window.localStorage.getItem(STORAGE_KEY));
}

function persistStore(store: FieldSessionStore) {
  if (typeof window === "undefined") return;
  const bounded = [...store.sessions]
    .sort((a, b) => b.updatedAt - a.updatedAt)
    .slice(0, MAX_SESSIONS);
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ schemaVersion: SCHEMA_VERSION, sessions: bounded }));
  } catch {
    // Field logging must never break GPS/AR if the browser quota is exhausted.
  }
}

function ensureSession(store: FieldSessionStore, link: FieldSessionLink): FieldSessionRecord {
  const existing = store.sessions.find((session) => session.sessionKey === link.sessionKey);
  const now = Date.now();
  if (existing) {
    existing.activityId = link.activityId;
    existing.routeId = link.routeId;
    existing.routeName = link.routeName;
    existing.recordingMode = link.recordingMode;
    if (existing.state !== "finished" && existing.state !== "discarded") existing.state = link.state;
    existing.updatedAt = now;
    return existing;
  }

  const created: FieldSessionRecord = {
    schemaVersion: SCHEMA_VERSION,
    fieldSessionId: crypto.randomUUID(),
    sessionKey: link.sessionKey,
    activityId: link.activityId,
    routeId: link.routeId,
    routeName: link.routeName,
    recordingMode: link.recordingMode,
    state: link.state,
    startedAt: link.startedAt,
    finishedAt: null,
    createdAt: now,
    updatedAt: now,
    routeSamples: [],
    arSamples: [],
    evidence: [],
  };
  store.sessions.push(created);
  return created;
}

export function fieldSessionLinkFromRouteContext(context: FieldRouteContext, standaloneSessionKey: string): FieldSessionLink {
  if (context.activityId) {
    return {
      sessionKey: context.activityId,
      activityId: context.activityId,
      routeId: context.routeId,
      routeName: context.routeName,
      recordingMode: context.recordingMode,
      startedAt: context.startedAt ?? Date.now(),
      state: context.state === "paused" ? "paused" : context.state === "recording" ? "recording" : "standalone",
    };
  }
  return {
    sessionKey: standaloneSessionKey,
    activityId: null,
    routeId: null,
    routeName: context.routeName || "Geo AR sin ruta activa",
    recordingMode: null,
    startedAt: Date.now(),
    state: "standalone",
  };
}

export function appendFieldRouteSample(link: FieldSessionLink, point: TrackPoint, distanceMeters: number): FieldSessionRecord | null {
  const cachedLastAt = lastRouteSampleAtBySession.get(link.sessionKey);
  if (cachedLastAt !== undefined && point.timestamp - cachedLastAt < ROUTE_SAMPLE_INTERVAL_MS) return null;

  const store = loadStore();
  const session = ensureSession(store, link);
  const last = session.routeSamples.at(-1);
  if (last && point.timestamp - last.timestamp < ROUTE_SAMPLE_INTERVAL_MS) {
    lastRouteSampleAtBySession.set(link.sessionKey, last.timestamp);
    return null;
  }

  session.routeSamples.push({
    timestamp: point.timestamp,
    latitude: point.latitude,
    longitude: point.longitude,
    altitude: point.altitude,
    accuracy: point.accuracy,
    activeSeconds: typeof point.activeSeconds === "number" ? point.activeSeconds : null,
    distanceMeters: Math.max(0, distanceMeters),
  });
  if (session.routeSamples.length > MAX_ROUTE_SAMPLES) {
    session.routeSamples = session.routeSamples.slice(-MAX_ROUTE_SAMPLES);
  }
  lastRouteSampleAtBySession.set(link.sessionKey, point.timestamp);
  session.updatedAt = Date.now();
  persistStore(store);
  return session;
}

export function appendFieldArSample(link: FieldSessionLink, sample: FieldArSample): FieldSessionRecord {
  const store = loadStore();
  const session = ensureSession(store, link);
  session.arSamples.push(sample);
  if (session.arSamples.length > MAX_AR_SAMPLES) session.arSamples = session.arSamples.slice(-MAX_AR_SAMPLES);
  session.updatedAt = Date.now();
  persistStore(store);
  return session;
}

export function appendFieldEvidence(link: FieldSessionLink, sample: FieldEvidenceSnapshot, input: {
  kind: FieldEvidenceKind;
  notes?: string;
  identifiedResult?: GeoArResult | null;
  metadata?: Record<string, string | number | boolean | null>;
}): FieldSessionRecord {
  const store = loadStore();
  const session = ensureSession(store, link);
  session.evidence.push({
    ...sample,
    evidenceId: crypto.randomUUID(),
    kind: input.kind,
    notes: input.notes?.trim() ?? "",
    identifiedResult: input.identifiedResult ?? null,
    metadata: input.metadata ?? {},
  });
  if (session.evidence.length > MAX_EVIDENCE) session.evidence = session.evidence.slice(-MAX_EVIDENCE);
  session.updatedAt = Date.now();
  persistStore(store);
  return session;
}

export function finalizeFieldSession(sessionKey: string, state: "finished" | "discarded" = "finished") {
  const store = loadStore();
  const session = store.sessions.find((item) => item.sessionKey === sessionKey);
  if (!session) return;
  session.state = state;
  session.finishedAt = Date.now();
  lastRouteSampleAtBySession.delete(sessionKey);
  session.updatedAt = Date.now();
  persistStore(store);
}

export function getFieldSession(sessionKey: string): FieldSessionRecord | null {
  const store = loadStore();
  return store.sessions.find((session) => session.sessionKey === sessionKey) ?? null;
}

export function fieldSessionCounts(sessionKey: string) {
  const session = getFieldSession(sessionKey);
  return {
    routeSamples: session?.routeSamples.length ?? 0,
    arSamples: session?.arSamples.length ?? 0,
    evidence: session?.evidence.length ?? 0,
  };
}

function xmlText(value: string) {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&apos;");
}

function trackToGpx(points: TrackPoint[], name: string) {
  const content = points.map((point) => {
    const elevation = point.altitude === null ? "" : `<ele>${point.altitude}</ele>`;
    const time = Number.isFinite(point.timestamp) ? `<time>${new Date(point.timestamp).toISOString()}</time>` : "";
    return `<trkpt lat="${point.latitude}" lon="${point.longitude}">${elevation}${time}</trkpt>`;
  }).join("");
  return `<?xml version="1.0" encoding="UTF-8"?><gpx version="1.1" creator="THERA Field Session Logger V1" xmlns="http://www.topografix.com/GPX/1/1"><trk><name>${xmlText(name)}</name><trkseg>${content}</trkseg></trk></gpx>`;
}

function safeName(value: string) {
  return value.toLocaleLowerCase("es")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "ruta";
}

export async function exportFieldSessionFiles(link: FieldSessionLink, fullTrack: TrackPoint[] = []) {
  const session = getFieldSession(link.sessionKey);
  if (!session) return null;
  const stamp = new Date().toISOString().replace(/[:.]/g, "-");
  const prefix = `thera-${safeName(session.routeName)}-${stamp}`;
  const files: TextExportFile[] = [
    {
      contents: JSON.stringify({
        exportType: "THERA_FIELD_SESSION_V1",
        exportedAt: new Date().toISOString(),
        session: { ...session, arSamples: undefined, evidence: undefined },
      }, null, 2),
      mimeType: "application/json",
      fileName: `${prefix}-field-session.json`,
    },
    {
      contents: JSON.stringify({
        exportType: "THERA_GEO_AR_SAMPLES_V1",
        exportedAt: new Date().toISOString(),
        fieldSessionId: session.fieldSessionId,
        activityId: session.activityId,
        routeId: session.routeId,
        samples: session.arSamples,
      }, null, 2),
      mimeType: "application/json",
      fileName: `${prefix}-geo-ar-samples.json`,
    },
    {
      contents: JSON.stringify({
        exportType: "THERA_FIELD_EVIDENCE_V1",
        exportedAt: new Date().toISOString(),
        fieldSessionId: session.fieldSessionId,
        activityId: session.activityId,
        routeId: session.routeId,
        evidence: session.evidence,
      }, null, 2),
      mimeType: "application/json",
      fileName: `${prefix}-evidence.json`,
    },
  ];

  const gpxTrack = fullTrack.length >= 2
    ? fullTrack
    : session.routeSamples.map((sample) => ({
        latitude: sample.latitude,
        longitude: sample.longitude,
        altitude: sample.altitude,
        accuracy: sample.accuracy,
        timestamp: sample.timestamp,
        activeSeconds: sample.activeSeconds ?? undefined,
      }));
  if (gpxTrack.length >= 2) {
    files.push({
      contents: trackToGpx(gpxTrack, session.routeName),
      mimeType: "application/gpx+xml",
      fileName: `${prefix}-route.gpx`,
    });
  }

  const exported = [];
  for (const file of files) exported.push(await exportTextFile(file));
  return {
    exported: exported.length,
    fileNames: exported.map((item) => item.fileName),
    location: exported[0]?.location ?? "Descargas",
    native: exported.some((item) => item.native),
  };
}
